import { ChangeEvent, FC, useCallback, useState } from 'react';
import { compact, difference, sumBy, values, without } from 'lodash';

import { FACET, FacetKeys, FACETS_ORDER } from '@app/components';
import { useSearchContext } from '@app/contexts';
import { useFeatureFlag, useLocationsFetch } from '@app/hooks';
import { FEATURE } from '@app/constants';

import { useFacetsRequest } from './useFacetsRequest';
import { Facet } from './Facet';
import { FacetAttribute } from './FacetAttribute';
import { FilterPanelContent } from './FilterPanelContent';

export const FilterPanel: FC = () => {
  const statusSearchFacetFlag = useFeatureFlag({
    name: FEATURE.statusSearchFacet,
  });
  const consortiaLoginFlowFlag = useFeatureFlag({
    name: FEATURE.consortiaLoginFlow,
  });
  const facetsOrder = without(
    FACETS_ORDER,
    ...compact([
      !statusSearchFacetFlag.isActive && FACET.STATUS,
      consortiaLoginFlowFlag.isActive && FACET.LOCATION,
    ])
  );

  const [facetsToFetch, setFacetsToFetch] = useState<FacetKeys[]>([]);
  const [isStatePristine, setStatePristine] = useState(true);
  const { selectedFacets, setSelectedFacets } = useSearchContext();
  const locationsQuery = useLocationsFetch();

  const facetGroups = useFacetsRequest({
    expandedFacets: facetsToFetch,
    isStatePristine,
  });

  const getFacetSelectionCount = useCallback(
    facetKey => compact(values(selectedFacets[facetKey])).length,
    [selectedFacets]
  );

  const selectedFiltersCount = sumBy(facetsOrder, facetKey =>
    getFacetSelectionCount(facetKey)
  );

  const toggleFacetAttribute = useCallback(
    (facetKey, value) => {
      setSelectedFacets(prevValue => ({
        ...prevValue,
        [facetKey]: {
          ...prevValue[facetKey],
          [value]: !prevValue[facetKey][value],
        },
      }));
    },
    [setSelectedFacets]
  );

  const isFacetAttributeSelected = useCallback(
    (facetKey, value) => Boolean(selectedFacets[facetKey]?.[value]),
    [selectedFacets]
  );

  const generateFacetAttributes = useCallback(
    (facetKey, facetValues) =>
      facetValues?.map(({ label, value, count }) => (
        <FacetAttribute
          key={`${facetKey}-${value}`}
          label={label}
          value={value}
          selectedCount={count}
          isChecked={isFacetAttributeSelected(facetKey, value)}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            toggleFacetAttribute(facetKey, e.target.value)
          }
        />
      )),
    [toggleFacetAttribute, isFacetAttributeSelected]
  );

  const expandFacet = useCallback(
    (facetKey: (typeof facetsOrder)[0]) => {
      const wasFacetExpandedBefore = facetsToFetch.includes(facetKey);

      if (!wasFacetExpandedBefore) {
        setStatePristine(false);
        setFacetsToFetch(prevState => prevState.concat(facetKey));
      }
    },
    [facetsToFetch]
  );

  const closeFacet = useCallback(
    (facetKey: (typeof facetsOrder)[0]) => {
      if (!getFacetSelectionCount(facetKey)) {
        setFacetsToFetch(prevState => difference(prevState, [facetKey]));
      }
    },
    [getFacetSelectionCount]
  );

  const renderFacets = useCallback(() => {
    return facetsOrder.map(facetKey => {
      const {
        label,
        values: facetValues,
        isError = false,
      } = facetGroups[facetKey];
      const selectedCount = getFacetSelectionCount(facetKey);
      const isFacetExpanded = selectedCount > 0;
      const isLocationFacet = facetKey === 'items.effectiveLocationId';
      const isFacetActive = isLocationFacet
        ? Boolean(locationsQuery.data)
        : true;

      return (
        <Facet
          key={facetKey}
          label={label}
          facetKey={facetKey}
          selectedCount={selectedCount}
          facetAttributes={generateFacetAttributes(facetKey, facetValues)}
          isInitiallyExpanded={isFacetExpanded}
          isError={isError}
          isActive={isFacetActive}
          expandFacet={expandFacet}
          closeFacet={closeFacet}
        />
      );
    });
  }, [
    facetsOrder,
    facetGroups,
    getFacetSelectionCount,
    locationsQuery.data,
    generateFacetAttributes,
    expandFacet,
    closeFacet,
  ]);

  return (
    <FilterPanelContent
      selectedFiltersCount={selectedFiltersCount}
      facets={facetGroups}
      renderFacets={renderFacets}
      toggleFacetAttribute={toggleFacetAttribute}
    />
  );
};
